import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api.ts'
import { MULTI, VALUELESS, type FieldMeta, type FieldNode } from './model.ts'

/**
 * The value control for one condition, chosen by the field's type.
 *
 * Enum fields get their real values from the server with counts beside them.
 * That is not decoration: CLAUDE.md is emphatic that code vocabularies are
 * discovered from data rather than assumed, so a free-text box here would
 * invite someone to type "Democrat" and get a confident zero.
 */

interface FieldValues {
  values: { value: string; count: number }[]
}

const controlClass =
  'w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink ' +
  'outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25'

export function ValueInput({
  node,
  field,
  onChange,
}: {
  node: FieldNode
  field: FieldMeta
  onChange: (value: FieldNode['value']) => void
}) {
  const wantsList = MULTI.has(node.cmp)

  const { data } = useQuery({
    queryKey: ['field-values', node.field],
    queryFn: () => apiGet<FieldValues>(`/universe/values/${node.field}`),
    // Only enum-ish fields have a usable value list; a distinct list of surnames
    // would be tens of thousands of rows and useless as a picker.
    enabled: field.kind === 'enum',
    staleTime: 5 * 60_000,
  })

  if (VALUELESS.has(node.cmp)) {
    return <span className="text-sm text-ink-muted">—</span>
  }

  if (field.kind === 'bool') {
    return (
      <select
        aria-label="Value"
        className={controlClass}
        value={node.value === true ? 'true' : node.value === false ? 'false' : ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}
      >
        <option value="">Choose…</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    )
  }

  if (node.cmp === 'between') {
    const pair = Array.isArray(node.value) ? node.value : []
    const type = field.kind === 'date' ? 'date' : 'number'
    return (
      <div className="flex items-center gap-2">
        <input
          aria-label="From"
          type={type}
          className={controlClass}
          value={String(pair[0] ?? '')}
          onChange={(e) => onChange([coerce(e.target.value, field), pair[1] ?? ''])}
        />
        <span className="text-xs text-ink-muted">to</span>
        <input
          aria-label="To"
          type={type}
          className={controlClass}
          value={String(pair[1] ?? '')}
          onChange={(e) => onChange([pair[0] ?? '', coerce(e.target.value, field)])}
        />
      </div>
    )
  }

  if (field.kind === 'enum' && data) {
    const selected = new Set((Array.isArray(node.value) ? node.value : [node.value]).map(String))
    if (wantsList) {
      return (
        <div className="max-h-36 overflow-y-auto rounded-md border border-line bg-surface p-1.5">
          {data.values.length === 0 ? (
            <p className="px-1 py-0.5 text-xs text-ink-muted">No values in the data.</p>
          ) : (
            data.values.map((option) => (
              <label key={option.value} className="flex items-center gap-2 px-1 py-0.5 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={selected.has(option.value)}
                  onChange={(e) => {
                    const next = new Set(selected)
                    if (e.target.checked) next.add(option.value)
                    else next.delete(option.value)
                    onChange([...next])
                  }}
                />
                <span className="font-mono text-xs">{option.value}</span>
                <span className="ml-auto text-xs tabular-nums text-ink-muted">
                  {option.count.toLocaleString()}
                </span>
              </label>
            ))
          )}
        </div>
      )
    }

    return (
      <select
        aria-label="Value"
        className={controlClass}
        value={String(node.value ?? '')}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">Choose…</option>
        {data.values.map((option) => (
          <option key={option.value} value={option.value}>
            {option.value} ({option.count.toLocaleString()})
          </option>
        ))}
      </select>
    )
  }

  return (
    <input
      aria-label="Value"
      type={field.kind === 'date' ? 'date' : field.kind === 'int' ? 'number' : 'text'}
      className={controlClass}
      value={String(node.value ?? '')}
      onChange={(e) => onChange(coerce(e.target.value, field))}
      placeholder={field.kind === 'date' ? 'YYYY-MM-DD' : ''}
    />
  )
}

/** Numbers must reach the AST as numbers; the compiler type-checks them. */
function coerce(raw: string, field: FieldMeta): string | number {
  if (field.kind !== 'int' || raw === '') return raw
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : raw
}
